import React, { useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { DockviewReact } from 'dockview';
import 'dockview/dist/styles/dockview.css';

import LoginPage from '../features/auth/LoginPage';
import SignupPage from '../features/auth/SignupPage';
import InviteHandler from '../features/auth/InviteHandler';
import ForgotPasswordPage from '../features/auth/ForgotPasswordPage';
import VerifyResetCodePage from '../features/auth/VerifyResetCodePage';
import NewPasswordPage from '../features/auth/NewPasswordPage';
import AuthGate from '../features/auth/AuthGate';
import GuestGate from '../features/auth/GuestGate';

import Sidebar from '../components/Sidebar';
import AIChatSidebar from '../components/chat/AIChatSidebar';
import { AgentChatProvider } from '../contexts/AgentChatContext';
import { dockviewComponents } from '../components/panels/DockviewPanels';
import { useTabManagement } from '../hooks/useTabManagement';
import { useTheme } from '../contexts';
import { MessageSquare } from 'lucide-react';

const MainLayout: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { onReady, addTab, popOutTab } = useTabManagement();
  const { resolvedTheme } = useTheme();

  // Listen for openAIChat events from landing page
  React.useEffect(() => {
    const handler = () => {
      setIsChatOpen(true);
    };
    window.addEventListener('openAIChat', handler);
    return () => window.removeEventListener('openAIChat', handler);
  }, []);

  return (
    <AgentChatProvider>
      <div className="h-screen flex flex-col bg-theme-bg-primary font-figtree overflow-hidden transition-colors duration-200">
        <div className="flex-1 flex min-h-0">
          <Sidebar
            onAddTab={addTab}
            onPopOutTab={popOutTab}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            onToggleChat={() => setIsChatOpen((prev) => !prev)}
          />
          <div className="flex-1 flex min-h-0 overflow-hidden">
            <div className="flex-1 min-w-0 min-h-0">
              <DockviewReact
                onReady={onReady}
                components={dockviewComponents}
                className={resolvedTheme === 'dark' ? 'dockview-theme-dark' : 'dockview-theme-light'}
              />
            </div>
            {/* AI Chat: vertical tab bar when closed, panel when open */}
            <div
              className="flex-shrink-0 h-full transition-[width] duration-300 ease-in-out overflow-hidden"
              style={{ width: isChatOpen ? '384px' : '36px' }}
            >
              {!isChatOpen ? (
                <button
                  onClick={() => setIsChatOpen(true)}
                  className="h-full w-[36px] flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors border-l"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-border)',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface)';
                  }}
                  title="Open AI Assistant"
                >
                  <MessageSquare className="w-4 h-4 text-engramiq-green" />
                  <span
                    className="text-[10px] font-semibold tracking-wider uppercase"
                    style={{
                      color: 'var(--color-text-muted)',
                      writingMode: 'vertical-rl',
                    }}
                  >
                    AI Chat
                  </span>
                </button>
              ) : (
                <AIChatSidebar
                  isOpen={isChatOpen}
                  onClose={() => setIsChatOpen(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </AgentChatProvider>
  );
};

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<GuestGate><LoginPage /></GuestGate>} />
      <Route path="/signup" element={<GuestGate><SignupPage /></GuestGate>} />
      <Route path="/invite" element={<InviteHandler />} />
      <Route path="/forgot-password" element={<GuestGate><ForgotPasswordPage /></GuestGate>} />
      <Route path="/reset-password/verify" element={<GuestGate><VerifyResetCodePage /></GuestGate>} />
      <Route path="/reset-password/new" element={<GuestGate><NewPasswordPage /></GuestGate>} />
      <Route
        path="/*"
        element={
          <AuthGate>
            <MainLayout key="main-layout" />
          </AuthGate>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
